'use client';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { Button } from '@shared/components/ui';
import { DateRangePicker } from '@shared/components/ui/DateRangePicker';
import { cn } from '@shared/utils/utils';
import { useCartsSimple } from '@modules/Carts/hooks';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRelocationsFilters } from '../../hooks/useRelocationsFilters';

interface RelocationsFiltersPanelProps {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
  className?: string;
}

export const RelocationsFiltersPanel = ({
  initialConditions = [],
  onChange,
  className,
}: RelocationsFiltersPanelProps) => {
  const { t } = useTranslation('relocationsModule');
  const { filters, handleStatusChange, handleCartIdChange, handleCreatedAtChange, clearAllFilters } =
    useRelocationsFilters({ initialConditions, onChange });
  const { carts } = useCartsSimple({ limit: 100 });

  const statusOptions = useMemo(
    () => [
      { value: '', label: t('filters.all_status') },
      { value: 'draft', label: t('filters.status_options.draft') },
      { value: 'dispatched', label: t('filters.status_options.dispatched') },
      { value: 'cancelled', label: t('filters.status_options.cancelled') },
    ],
    [t],
  );

  const cartOptions = useMemo(() => {
    const base = [{ value: '', label: t('filters.all_carts') }];
    return base.concat(
      carts.map((cart) => ({
        value: String(cart.id),
        label: cart.code,
      })),
    );
  }, [carts, t]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.status) count++;
    if (filters.cart_id) count++;
    if (filters.created_at?.from && filters.created_at?.to) count++;
    return count;
  }, [filters]);

  return (
    <div className={cn('space-y-5', className)}>
      {/* Filtros alineados con GET /relocations - Relocations API filters */}
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('filters.status')}>
            <Combobox
              options={statusOptions}
              value={filters.status}
              setValue={handleStatusChange}
              placeholder={t('filters.select_status')}
              dataTestId="relocations-filter-status"
            />
          </FilterField>

          <FilterField label={t('filters.cart')}>
            <Combobox
              options={cartOptions}
              value={filters.cart_id}
              setValue={handleCartIdChange}
              placeholder={t('filters.select_cart')}
              dataTestId="relocations-filter-cart"
            />
          </FilterField>

          <FilterField label={t('filters.date')}>
            <DateRangePicker
              initialDateFrom={filters.created_at?.from}
              initialDateTo={filters.created_at?.to}
              onUpdate={({ range }) => handleCreatedAtChange(range)}
              placeholder={t('filters.date_placeholder')}
              dataTestId="relocations-filter-dates"
              triggerClassName="h-[38px] text-sm"
            />
          </FilterField>
        </div>
      </div>

      {activeFiltersCount > 0 && (
        <div className="flex items-center justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={clearAllFilters}
            type="button"
            data-testid="relocations-clear-filters"
          >
            {t('filters.clear')} ({activeFiltersCount})
          </Button>
        </div>
      )}
    </div>
  );
};

const FilterField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);
